import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import clsx from 'clsx';
import { EditorPopupItem, TextEditorStyle } from 'components/Component.style';
import { ReactComponent as Exclamation } from 'assert/images/Common/exclamation.svg';
import { useDispatch } from 'react-redux';
import {
  getHashtagList,
  getUsernameMentionList,
} from 'redux/features/ProfileSlice';

const TextEditor = forwardRef(
  (
    {
      label,
      required,
      errMsg,
      placeholder,
      onChange,
      value,
      Cusmodules,
      maxLength = 3000,
      customStyle,
      handleGetUserItem,
      callbackTextLength,
      enableMention = false,
      enableHashtag = false,
    },
    ref
  ) => {
    const dispatch = useDispatch();
    const reactQuillRef = useRef(null);
    const debounceTimeoutRef = useRef(null);
    // const [textLength, setTextLength] = useState(0);
    const [showList, setShowList] = useState(false);
    const [listType, setListType] = useState('');
    const [popupSearch, setPopupSearch] = useState('');
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const [currentRange, setCurrentRange] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [mentions, setMentions] = useState([]);
    const [hashtags, setHashtags] = useState([]);
    const [loading, setLoading] = useState(false);

    useImperativeHandle(ref, () => ({
      insertMentionAtCursor,
    }));

    const modules = {
      toolbar: {
        container: [
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }, { align: [] }],
          ['link', 'code-block', 'clean'],
        ],
      },
    };

    useEffect(() => {
      const quill = reactQuillRef.current?.getEditor();
      if (!quill) return;

      quill.on('selection-change', (range) => {
        if (range && range.length === 0) {
          quill.format('bold', false);
          quill.format('italic', false);
          quill.format('underline', false);
          quill.format('color', false);
        }
      });

      quill.on('text-change', (delta, oldDelta, source) => {
        if (source === 'user') {
          if (quill.getLength() > maxLength) {
            quill.deleteText(maxLength, quill.getLength());
          }

          const selection = quill.getSelection();
          if (selection) {
            const textBeforeCursor = quill.getText(0, selection.index);
            const match = textBeforeCursor.match(/([@#])(\w+)$/);

            if (match) {
              const symbol = match[1];
              const keyword = match[2];

              if (
                (symbol === '#' && !enableHashtag) ||
                (symbol === '@' && !enableMention)
              ) {
                setShowList(false);
                return;
              }

              const bounds = quill.getBounds(selection.index);

              setPosition({
                top: bounds.top + bounds.height + 35,
                left: bounds.left,
              });
              setShowList(true);
              setCurrentRange(selection);
              setPopupSearch(keyword);

              if (symbol === '#' && enableHashtag) {
                setListType('hashtag');
                debounce(() => handleSearchHashtag(keyword), 500);
              } else if (symbol === '@' && enableMention) {
                setListType('mention');
                debounce(() => handleSearchUsername(keyword), 500);
              }
            } else {
              setShowList(false);
            }
          }

          highlightMentionsAndHashtags(quill);
          applyFontWeightToColoredText();

          if (delta.ops?.some((op) => op.delete)) {
            const range = quill.getSelection(true);
            if (range) {
              quill.formatText(
                range.index,
                1,
                { bold: false, italic: false, underline: false, color: false },
                'user'
              );
            }
          }
        }
      });

      return () => {
        quill.off('text-change');
        quill.off('selection-change');
      };
    }, [enableMention, enableHashtag, hashtags, mentions]);

    const debounce = (fn, delay) => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = setTimeout(fn, delay);
    };

    const highlightMentionsAndHashtags = (quill) => {
      const text = quill.getText();

      if (enableHashtag) {
        [...text.matchAll(/#\w+\b/g)].forEach((match) => {
          if (match.index !== undefined) {
            const start = match.index;
            const length = match[0].length;
            quill.formatText(
              start,
              length,
              { color: '#08979c', bold: true },
              'silent'
            );
            quill.formatText(
              start + length,
              1,
              { color: false, bold: false },
              'silent'
            );
          }
        });
      }
    };

    const insertMentionAtCursor = (username) => {
      if (!enableMention) return;
      const quill = reactQuillRef.current?.getEditor();
      const range = quill?.getSelection(true);
      if (!quill || !range) return;

      const mentionText = `@${username}`;
      quill.insertText(
        range.index,
        mentionText,
        { bold: true, color: '#08979c' },
        'user'
      );
      // quill.insertText(range.index + mentionText.length, ' ', 'user');
      // quill.setSelection(range.index + mentionText.length + 1);
      const afterIndex = range.index + mentionText.length;
      quill.insertText(afterIndex, ' ', { bold: false, color: false }, 'user');
      quill.setSelection(afterIndex + 1);
    };

    const applyFontWeightToColoredText = () => {
      const quillEl = document.querySelector('.ql-editor');
      if (!quillEl) return;
      quillEl
        .querySelectorAll('[style*="color: rgb(8, 151, 156)"]')
        .forEach((el) => {
          el.style.fontSize = '14px';
        });
      quillEl
        .querySelectorAll('[style*="color: rgb(25, 118, 210)"]')
        .forEach((el) => {
          el.style.fontSize = '14px';
        });
    };

    const handleEditorChange = (content) => {
      const quill = reactQuillRef.current?.getEditor();
      const currentLength = quill?.getLength() - 1 || 0;
      if (maxLength && currentLength > maxLength) {
        quill.deleteText(maxLength, quill.getLength());
      } else {
        onChange(content.replace(/<img[^>]*>/gi, ''), currentLength);
      }
    };

    const handleSearchUsername = (value) => {
      if (value.length < 1) return;
      setLoading(true);
      dispatch(getUsernameMentionList(value))
        .then((res) => {
          setLoading(false);
          const formatted =
            res?.rows?.data?.map((item) => ({
              id: item.id,
              value: item.username,
              type: 'mention',
            })) || [];
          setMentions(formatted);
        })
        .catch(() => setLoading(false));
    };

    const handleSearchHashtag = (value) => {
      if (value.length < 1) return;
      setLoading(true);
      dispatch(getHashtagList(value))
        .then((res) => {
          setLoading(false);
          const formatted =
            res?.map((item) => ({
              id: item.id,
              value: item.name,
              type: 'hashtag',
            })) || [];
          setHashtags(formatted);
        })
        .catch(() => setLoading(false));
    };

    const getFilteredList = () => {
      const list = listType === 'mention' ? mentions : hashtags;
      return list.filter((item) =>
        item.value.toLowerCase().includes(popupSearch.toLowerCase())
      );
    };

    const handleKeyDown = (e) => {
      if (
        !showList ||
        (!enableMention && listType === 'mention') ||
        (!enableHashtag && listType === 'hashtag')
      )
        return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % getFilteredList().length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(
          (prev) =>
            (prev - 1 + getFilteredList().length) % getFilteredList().length
        );
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (getFilteredList().length > 0) {
          handleSelectItem(getFilteredList()[selectedIndex]);
        }
      }
    };

    const handleSelectItem = (item) => {
      const quill = reactQuillRef.current?.getEditor();
      if (!quill || !currentRange) return;

      let insertIndex = currentRange.index;
      const textBeforeCursor = quill.getText(0, currentRange.index);

      if (listType === 'hashtag') {
        const match = textBeforeCursor.match(/([#])(\w*)$/);
        if (match?.index !== undefined) {
          insertIndex = match.index;
          quill.deleteText(insertIndex, currentRange.index - insertIndex);
        }
        const insertText = `#${item.value}`;
        quill.insertText(
          insertIndex,
          insertText,
          { bold: true, color: '#08979c' },
          'user'
        );
        quill.insertText(insertIndex + insertText.length, ' ', 'user');
        quill.setSelection(insertIndex + insertText.length + 1);
      }

      if (listType === 'mention') {
        const match = textBeforeCursor.match(/@(\w*)$/);
        if (match?.index !== undefined) {
          insertIndex = match.index;
          quill.deleteText(insertIndex, currentRange.index - insertIndex);
        }
        const mentionText = `@${item?.value}`;
        quill.insertText(
          insertIndex,
          mentionText,
          { bold: true, color: '#1976d2' },
          'user'
        );
        // quill.insertText(insertIndex + mentionText.length, ' ', 'user');
        // quill.setSelection(insertIndex + mentionText.length + 1);
        const afterIndex = insertIndex + mentionText.length;
        quill.insertText(
          afterIndex,
          ' ',
          { bold: false, color: false },
          'user'
        );
        quill.setSelection(afterIndex + 1);
        handleGetUserItem && handleGetUserItem(item);
      }

      setShowList(false);
      setPopupSearch('');
    };

    const quill = reactQuillRef.current?.getEditor();
    const currentTextLength = quill?.getText()?.trim()?.length || 0;
    callbackTextLength?.(currentTextLength);

    return (
      <TextEditorStyle className="text-editor" style={{ position: 'relative' }}>
        {label && (
          <div className="lebelView">
            <label>
              {label} {required && <span className="requiredSymbol">*</span>}
            </label>
          </div>
        )}
        <ReactQuill
          ref={reactQuillRef}
          className={clsx(customStyle)}
          theme="snow"
          modules={Cusmodules || modules}
          placeholder={placeholder}
          onChange={handleEditorChange}
          onKeyDown={handleKeyDown}
          onPaste={() =>
            setTimeout(
              () =>
                highlightMentionsAndHashtags(
                  reactQuillRef.current?.getEditor()
                ),
              100
            )
          }
          value={value}
          style={{ minHeight: '180px' }}
        />
        <div className="textlength">
          {currentTextLength}/{maxLength}
        </div>
        {errMsg && (
          <span className="errMsg">
            <Exclamation />
            {errMsg}
          </span>
        )}
        {showList && (
          <div
            className="editor-popup"
            style={{
              top: position.top,
              left: position.left,
              borderWidth:
                (!loading && getFilteredList().length === 0) ||
                getFilteredList().length > 0
                  ? 1
                  : 0,
            }}
          >
            {loading && (
              <div style={{ padding: '8px 12px', color: '#666' }}>
                Loading...
              </div>
            )}
            {getFilteredList().map((item, idx) => (
              <EditorPopupItem
                key={item.id + item.value}
                onClick={() => handleSelectItem(item)}
                className="editor-popup-item"
                isfocus={idx === selectedIndex}
              >
                {item.type === 'mention' ? '@' : '#'}
                {item.value}
              </EditorPopupItem>
            ))}
            {!loading && getFilteredList().length === 0 && (
              <div style={{ padding: '8px 12px', color: '#999' }}>
                No matches found
              </div>
            )}
          </div>
        )}
      </TextEditorStyle>
    );
  }
);

TextEditor.displayName = 'TextEditor';
export default TextEditor;
